import { __decorate } from "tslib";
import { debounce } from 'lodash';
import { mixins } from 'vue-class-component';
import { Component, Watch, Prop } from 'vue-property-decorator';
import ConfirmationDialog from '@/components/ConfirmationDialog.vue';
import FormMixin from '@/helpers/vue_mixins/FormMixin';
import { Getter, Mutation } from 'vuex-class';
import { successfulDestroy, successfulUpdate } from '@/helpers/mixins/LocalizationHelper';
import AlertMixin from '@/helpers/vue_mixins/AlertMixin';
import RouterParamsAppMixin from '@/helpers/vue_mixins/RouterParamsAppMixin';
import PaginatorMixin from '@/helpers/mixins/PaginatorMixin';
import BaseOverlayLoader from '@/components/v2/BaseOverlayLoader';
import Paginator from '@/components/v2/Paginator.vue';
import CleverTapEventsMixin from '@/helpers/vue_mixins/v3/CleverTapEventsMixin';
import Faqs from '@/plugins/graphql/queries/v2/faqs.gql';
import { FaqModel } from '@/helpers/entities/models/FaqModel';
import FaqDelete from '@/plugins/graphql/mutations/v2/faq-delete.gql';
import FaqUpdateStatus from '@/plugins/graphql/mutations/v2/faq-update-status.gql';
import faqUpdatePosition from '@/plugins/graphql/mutations/v2/faq-update-position.gql';
import { PAGINATION_OPTIONS } from '@/helpers/constants/app';
let FaqManager = class FaqManager extends mixins(FormMixin, AlertMixin, RouterParamsAppMixin, PaginatorMixin, CleverTapEventsMixin) {
    constructor() {
        super(...arguments);
        this.faqs = [];
        this.currentPage = 0;
        this.selectedFaqUuid = null;
        this.startCursor = null;
        this.paginationLimit = 10;
        this.debouncedFetchData = debounce((query) => {
            const newLimit = !query ? 10 : 'All';
            this.handlePaginationLimitChange(newLimit);
        }, 500);
    }
    handleStartChanged(start) {
        this.currentPage = start;
    }
    toggleConfirmation(uuid) {
        this.selectedFaqUuid = uuid;
        this.showConfirmationDialog();
    }
    goToFaqDetailsPage(uuid) {
        this.$router.push({
            name: 'faq-edit',
            params: {
                uuid,
            },
        });
    }
    get queryVariables() {
        return {
            appUuid: this.appUuid,
            first: this.paginationLimit === 'All' ? this.totalCount : this.paginationLimit,
            after: this.startCursor,
        };
    }
    get pageTitle() {
        return this.$route.meta.title;
    }
    paginationOptions(totalCount) {
        const index = PAGINATION_OPTIONS.findIndex((v) => v >= totalCount) + 1;
        return index === 0
            ? PAGINATION_OPTIONS.concat(['All'])
            : PAGINATION_OPTIONS.slice(0, index).concat(['All']);
    }
    get computedPaginationLimit() {
        return this.paginationLimit === 'All' ? this.totalCount : this.paginationLimit;
    }
    toggleUpdateStatus(active, faqUuid) {
        this.updateFaq(active, faqUuid);
    }
    async updateFaq(active, faqUuid) {
        this.requestOnFlight();
        await this.updateActiveStatus(active, faqUuid)
            .then(() => {
            const msg = successfulUpdate('FAQ');
            this.successNotification(msg);
        })
            .catch((e) => {
            this.errorNotification(e.message);
        })
            .finally(() => {
            this.requestDone();
        });
    }
    async updateActiveStatus(active, faqUuid) {
        if (active === null) {
            active = false;
        }
        await this.$apollo.mutate({
            mutation: FaqUpdateStatus,
            variables: {
                input: {
                    faqUuid,
                    status: active,
                },
            },
        });
    }
    async uponConfirm() {
        this.requestOnFlight();
        await this.delete()
            .then(() => {
            const msg = successfulDestroy('FAQ');
            this.successNotification(msg);
            this.$apollo.queries.faqs.refetch();
        })
            .catch((e) => {
            this.errorNotification(e.message);
        })
            .finally(() => {
            this.requestDone();
            this.hideConfirmationDialog();
        });
    }
    async delete() {
        if (!this.selectedFaqUuid) {
            return;
        }
        await this.$apollo.mutate({
            mutation: FaqDelete,
            variables: {
                input: {
                    faqUuid: this.selectedFaqUuid,
                },
            },
        });
    }
    onPositionChanged(event) {
        const newPos = event.newIndex;
        const oldPos = event.oldIndex;
        const faqUuid = event.item.getAttribute('data-uuid');
        const pageMultiplier = Math.floor(this.currentPage / this.perPage);
        const changedPosition = pageMultiplier * this.perPage + newPos;
        const newPosition = changedPosition + 1;
        const oldPosition = oldPos + 1;
        if (newPosition !== oldPosition) {
            this.$apollo.mutate({
                mutation: faqUpdatePosition,
                variables: {
                    input: {
                        faqUuid,
                        position: newPosition,
                    },
                },
                refetchQueries: [
                    {
                        query: Faqs,
                        variables: this.queryVariables,
                    },
                ],
            });
        }
    }
    handlePaginationLimitChange(newLimit) {
        this.paginationLimit = newLimit;
        this.startCursor = null;
        this.currentPage = 1;
        this.fetchData();
    }
    handleSearchQueryChange(newQuery) {
        this.debouncedFetchData(newQuery);
    }
    fetchData() {
        if (this.$apollo.queries.faqs) {
            this.$apollo.queries.faqs.refetch().catch((error) => {
                console.error('Error refetching data:', error);
            });
        }
    }
    get filteredFaqs() {
        const query = (this.searchQuery || '').trim();
        if (!query) {
            return this.faqs;
        }
        return this.faqs.filter((faq) => {
            return faq.answer.toLowerCase().includes(query.toLowerCase()) ||
                faq.question.toLowerCase().includes(query.toLowerCase());
        });
    }
    stripHtmlTags(content) {
        return content.replace(/<[^>]*>/g, '');
    }
    mounted() {
        this.$parent.$on("faq-updated", this.refetchFaqs);
    }
    refetchFaqs() {
        this.$apollo.queries.faqs.refetch();
    }
};
__decorate([
    Getter('appUuid')
], FaqManager.prototype, "appUuid", void 0);
__decorate([
    Mutation('showConfirmationDialog')
], FaqManager.prototype, "showConfirmationDialog", void 0);
__decorate([
    Mutation('hideConfirmationDialog')
], FaqManager.prototype, "hideConfirmationDialog", void 0);
__decorate([
    Prop({ default: '' })
], FaqManager.prototype, "searchQuery", void 0);
__decorate([
    Watch('paginationLimit', { immediate: true })
], FaqManager.prototype, "handlePaginationLimitChange", null);
__decorate([
    Watch('searchQuery', { immediate: true })
], FaqManager.prototype, "handleSearchQueryChange", null);
FaqManager = __decorate([
    Component({
        apollo: {
            faqs: {
                query: Faqs,
                variables() {
                    return this.queryVariables;
                },
                update({ fetchAllFaqs }) {
                    const { edges, pageInfo, totalCount } = fetchAllFaqs;
                    const faqNodes = (faq) => {
                        return new FaqModel(faq.node);
                    };
                    this.pageInfo = pageInfo;
                    this.totalCount = totalCount;
                    return edges.map(faqNodes);
                },
                fetchPolicy: 'cache-and-network',
            },
        },
        components: {
            ConfirmationDialog,
            BaseOverlayLoader,
            Paginator,
        },
    })
], FaqManager);
export default FaqManager;
